import { Injector, Input, OnInit, AfterViewInit, Directive } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs';

import { LocalStorageService } from 'ngx-webstorage';

import { NotificationService } from '../services/notification.service';
import { BaseComponent } from './base-component';
import { IsSaveRequired } from '../models';

@Directive()
export abstract class EntityBaseComponent<T> extends BaseComponent implements OnInit, IsSaveRequired {
  public get isFormInvalid(): boolean {
    if (this.entityForm) {
      return this.entityForm.invalid;
    }
    return true;
  }

  public get loading() {
    return this.entityForm.disabled;
  }
  public get entityForm(): FormGroup {
    return this._entityForm;
  }

  constructor(injector: Injector) {
    super(injector);
    const fb = injector.get(FormBuilder);
    this._entityForm = fb.group({});
    this.route = injector.get(ActivatedRoute);
    this.storage = injector.get(LocalStorageService);
    this.notificationService = injector.get(NotificationService);
  }

  @Input()
  public entity: T;

  public draft: T;

  public formDisabled;

  private _entityForm: FormGroup = null;

  protected routerCommands: (T) => any[];
  protected abstract updatedSuccessMessage;
  protected abstract updatedFailedMessage;
  protected route: ActivatedRoute;
  private storage: LocalStorageService;
  protected notificationService: NotificationService;

  isSaveRequired(): boolean {
    return this.entityForm && this.entityForm.dirty;
  }
  protected abstract updateService(entity: T): Observable<{}>;
  protected updateBreadcrumb(entity: T) {}

  ngOnInit() {
    this.route.data.subscribe((value) => {
      this.entity = value.entity || {};
      this.updateBreadcrumb(this.entity);

      this.entityForm.patchValue(this.entity, { onlySelf: true });
      this.entityForm.markAsPristine();
    });
    this.draft = this.storage.retrieve(`draft-${this.router.url}`);
  }

  update(entityForm: FormGroup) {
    this.formDisabled = true;

    //  Merge with properties loaded when entity was retreived
    Object.assign(this.entity, entityForm.getRawValue());

    this.updateService(this.entity).subscribe(
      (entity: T) => {
        entityForm.markAsPristine();
        this.entity = entity;

        this.updateBreadcrumb(this.entity);

        this.storage.clear(`draft-${this.router.url}`);
        this.draft = null;
        this.formDisabled = null;

        //  Update breadcrumb with entity name
        this.notificationService.success(this.updatedSuccessMessage);
      },
      (error) => {
        this.formDisabled = null;
        if (!this.handleError(error, entityForm)) {
          this.notificationService.error(this.updatedFailedMessage);
        }
      },
    );
  }

  create(entityForm: FormGroup) {
    this.formDisabled = true;

    //  Merge with properties loaded when entity was retreived
    Object.assign(this.entity, entityForm.getRawValue());

    this.updateService(this.entity).subscribe(
      (entity: T) => {
        entityForm.markAsPristine();
        //  Update breadcrumb with entity name
        if (this.routerCommands) {
          this.router.navigate(this.routerCommands(entity), { relativeTo: this.route });
        }

        this.notificationService.success(this.updatedSuccessMessage);
      },
      (error) => {
        this.formDisabled = null;
        if (!this.handleError(error, entityForm)) {
          this.notificationService.error(this.updatedFailedMessage);
        }
      },
    );
  }

  saveDraft(): void {
    const draftValue = Object.assign({}, this.entity, this.entityForm.getRawValue());
    this.storage.store(`draft-${this.router.url}`, draftValue);
    this.draft = draftValue;
  }

  loadDraft(): void {
    this.entityForm.markAsUntouched();
    this.entity = Object.assign({}, this.draft);
  }
}
